/*
 * Copyright (c) 2017-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

import * as React from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { toRelativeUrl } from '@okta/okta-auth-js';

interface SecureRouteProps {
  children: JSX.Element;
}

export function SecureRoute({ children }: SecureRouteProps) {
  const {
    oktaAuth,
    authState,
    _onAuthRequired: onAuthRequired,
  } = useOktaAuth();
  const pendingLogin = React.useRef(false);
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/naming-convention, @typescript-eslint/no-unused-vars
  const [_handleLoginError, setHandleLoginError] = React.useState<Error | null>(
    null,
  );
  // const ErrorReporter = errorComponent || OktaError;

  React.useEffect(() => {
    const handleLogin = async () => {
      if (pendingLogin.current) {
        return;
      }

      pendingLogin.current = true;

      const originalUri = toRelativeUrl(
        window.location.href,
        window.location.origin,
      );
      oktaAuth.setOriginalUri(originalUri);
      const onAuthRequiredFn = onAuthRequired;
      if (onAuthRequiredFn) {
        await onAuthRequiredFn(oktaAuth);
      } else {
        await oktaAuth.signInWithRedirect();
      }
    };

    // // Only process logic if the route matches
    // if (!match) {
    //   return;
    // }

    if (!authState) {
      return;
    }

    if (authState.isAuthenticated) {
      pendingLogin.current = false;
      return;
    }

    // Start login if app has decided it is not logged in and there is no pending signin
    if (!authState.isAuthenticated) {
      handleLogin().catch((err) => {
        setHandleLoginError(err as Error);
      });
    }
  }, [authState, oktaAuth, onAuthRequired]);

  // if (handleLoginError) {
  //   return <ErrorReporter error={handleLoginError} />;
  // }

  if (!authState || !authState.isAuthenticated) {
    return (
      <div
        style={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          flex: 1,
          justifyContent: 'center',
          alignContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        You do not have permission to access this site.
      </div>
    );
  }

  return children ?? null;
}
